import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { InlineButtonNodeView } from './inline-button-node-view'

export interface InlineButtonOptions {
  HTMLAttributes: Record<string, any>
  openOnClick: boolean
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    inlineButton: {
      /**
       * Insert an inline button
       */
      insertInlineButton: (options: {
        text?: string
        href?: string
        variant?: 'primary' | 'secondary'
        icon?: string
      }) => ReturnType
    }
  }
}

export const InlineButtonNode = Node.create<InlineButtonOptions>({
  name: 'inlineButton',

  priority: 1000,

  group: 'inline',

  inline: true,

  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      openOnClick: true,
    }
  },

  addAttributes() {
    return {
      text: {
        default: 'Button',
        parseHTML: element => element.getAttribute('data-text') || element.textContent || 'Button',
        renderHTML: attributes => ({
          'data-text': attributes.text,
        }),
      },
      href: {
        default: '',
        parseHTML: element => element.getAttribute('data-href') || element.getAttribute('href') || '',
        renderHTML: attributes => ({
          'data-href': attributes.href,
        }),
      },
      variant: {
        default: 'primary',
        parseHTML: element => element.getAttribute('data-variant') || 'primary',
        renderHTML: attributes => ({
          'data-variant': attributes.variant,
        }),
      },
      icon: {
        default: '',
        parseHTML: element => element.getAttribute('data-icon') || '',
        renderHTML: attributes => ({
          'data-icon': attributes.icon,
        }),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'button[data-inline-button]',
      },
      {
        tag: 'a[data-inline-button]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'button',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-inline-button': '',
        class: `inline-button inline-button--${HTMLAttributes.variant || 'primary'}`,
      }),
      HTMLAttributes.text || 'Button',
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(InlineButtonNodeView)
  },

  addCommands() {
    return {
      insertInlineButton:
        options =>
        ({ chain, state }) => {
          const { selection } = state
          const { from, to } = selection
          const text = state.doc.textBetween(from, to, ' ')

          return chain()
            .insertContent({
              type: this.name,
              attrs: {
                text: options.text || text || 'New Button',
                href: options.href || '',
                variant: options.variant || 'primary',
                icon: options.icon || '',
              },
            })
            .run()
        },
      insertInlineButtonWithEditor:
        (options: {
          text?: string
          href?: string
          variant?: 'primary' | 'secondary'
          icon?: string
        } = {}) =>
        ({ chain, state, editor }: any) => {
          const { selection } = state
          const { from, to } = selection
          const text = state.doc.textBetween(from, to, ' ')

          const result = chain()
            .insertContent({
              type: this.name,
              attrs: {
                text: options.text || text || 'New Button',
                href: options.href || '',
                variant: options.variant || 'primary',
                icon: options.icon || '',
              },
            })
            .run()
            
          if (result) {
            // After insertion, select the button and emit event to open editor
            setTimeout(() => {
              const { from: newFrom } = editor.state.selection
              const pos = newFrom - 1 // Button is inserted before current position
              
              editor.chain()
                .focus()
                .setNodeSelection(pos)
                .run()
              
              // Emit event to open editor
              editor.emit('openButtonEditor')
            }, 50)
          }
          
          return result
        },
    }
  },

  addProseMirrorPlugins() {
    const { options } = this

    return [
      new Plugin({
        key: new PluginKey('handleClickInlineButton'),
        props: {
          handleClick: (_view, _pos, event) => {
            if (options.openOnClick) {
              const target = event.target as HTMLElement
              const button = target.closest('button[data-inline-button]')
              
              if (button) {
                const href = button.getAttribute('data-href')
                
                if (href) {
                  event.preventDefault()
                  event.stopPropagation()
                  
                  // Handle internal links
                  if (href.startsWith('/')) {
                    window.location.href = href
                  } else {
                    window.open(href, '_blank', 'noopener,noreferrer')
                  }
                  
                  return true
                }
              }
            }

            return false
          },
        },
      }),
    ]
  },

  // For markdown serialization
  addStorage() {
    return {
      markdown: {
        serialize(state: any, node: any) {
          const { text, href, variant, icon } = node.attrs
          
          // Serialize as a markdown link with custom attributes
          if (href) {
            // Store button metadata in the link title attribute
            const metadata = JSON.stringify({ variant, icon, type: 'inline-button' })
            state.write(`[${text}](${href} "${metadata}")`)
          } else {
            // If no href, just write the text
            state.write(text)
          }
        },
        // properly deserialize buttons from markdown
        parse(token: any) {
          // Attempt to parse inline button metadata from the link title
          if (token && token.title) {
            try {
              const metadata = JSON.parse(token.title)
              if (metadata && metadata.type === 'inline-button') {
                return {
                  href: token.href,
                  text: token.text,
                  variant: metadata.variant || 'primary',
                  icon: metadata.icon || '',
                }
              }
            } catch (e) {
              // Not a valid JSON, fallback
            }
          }
          // Fallback: treat as a normal link with just href/text
          return {
            href: token.href,
            text: token.text,
            variant: 'primary',
            icon: '',
          }
        },
      },
    }
  },
})